import { Tables } from "@/types/database.types";

// Available avatar icons from Lucide
export const AVAILABLE_AVATAR_ICONS = [
  'BarChart',
  'ClipboardList', 
  'LifeBuoy',
  'DollarSign',
  'FileText',
  'Shield',
  'Umbrella',
  'Search',
  'MessageCircle',
  'Building',
  'ClipboardCheck',
  'Wrench',
  'Server',
  'FileCode',
  'Settings',
  'Target',
  'Compass',
  'Home',
  'Layout',
  'User'
];

// Interface for agent default values
export interface AgentDefaults {
  // Basic Information
  agent_name: string;
  title: string;
  description?: string;
  
  // Content & Messaging
  welcome_message: string;
  thank_you_message: string;
  conversation_starters: string[];
  instructions?: string;
  restrictions?: string;
  
  // Splash Page
  splash_page_title: string;
  splash_page_subtitle: string;
  start_button_text: string;
  
  // Visual & Branding
  avatar_url: string;
  
  // AI Settings
  ai_temperature: number;
  
  // Features & Capabilities
  allow_file_search: { enabled: boolean };
  allow_web_search: string;
  
  // System Settings
  is_active: boolean;
  is_template: boolean;
  is_public: boolean;
  goal: string;
  category: string;
  
  // Notification
  notification_email?: string;
  
  // Contact Info Settings
  contact_info: {
    requireName: boolean;
    requireEmail: boolean;
    requirePhoneNumber: boolean;
  };
}

/**
 * Get a random avatar icon from the available options
 */
export const getRandomAvatarIcon = (): string => {
  const randomIndex = Math.floor(Math.random() * AVAILABLE_AVATAR_ICONS.length);
  return AVAILABLE_AVATAR_ICONS[randomIndex];
};

/**
 * Generate default values for a new agent
 */
export const generateAgentDefaults = (
  organizationName: string,
  userEmail: string,
  organizationIndustryId?: string | null
): AgentDefaults => {
  return {
    // Basic Information
    agent_name: "My agent",
    title: "Custom Agent",
    description: undefined,
    
    // Content & Messaging
    welcome_message: `Welcome to ${organizationName}! How can I help you today?`,
    thank_you_message: "Thank you for reaching out. Our team has been notified of our conversation.",
    conversation_starters: [
      "Schedule a consultation",
      "Get pricing information", 
      "Learn about our services"
    ],
    instructions: undefined,
    restrictions: undefined,
    
    // Splash Page
    splash_page_title: `Welcome to ${organizationName}`,
    splash_page_subtitle: "Ask me anything, anytime—I'm here 24/7 to help.",
    start_button_text: "Start chat",
    
    // Visual & Branding
    avatar_url: getRandomAvatarIcon(),
    
    // AI Settings
    ai_temperature: 0.10,
    
    // Features & Capabilities
    allow_file_search: { enabled: true },
    allow_web_search: "disabled",
    
    // System Settings
    is_active: true,
    is_template: false,
    is_public: false,
    goal: "support",
    category: "unknown",
    
    // Notification
    notification_email: userEmail,
    
    // Contact Info Settings - Enable phone collection by default
    contact_info: {
      requireName: true,
      requireEmail: true,
      requirePhoneNumber: true
    },
  };
};

/**
 * Apply default values to agent data, filling in missing or empty properties
 */
export const applyAgentDefaults = (
  agentData: any,
  organizationName: string,
  userEmail: string,
  organizationIndustryId?: string | null
): any => {
  const defaults = generateAgentDefaults(organizationName, userEmail, organizationIndustryId);
  
  return {
    // Apply defaults only for missing or empty values
    agent_name: agentData.agent_name || defaults.agent_name,
    title: agentData.title || defaults.title,
    description: agentData.description || defaults.description,
    
    welcome_message: agentData.welcome_message || defaults.welcome_message,
    thank_you_message: agentData.thank_you_message || defaults.thank_you_message,
    conversation_starters: agentData.conversation_starters?.length ? agentData.conversation_starters : defaults.conversation_starters,
    instructions: agentData.instructions || defaults.instructions,
    restrictions: agentData.restrictions || defaults.restrictions,
    
    splash_page_title: agentData.splash_page_title || defaults.splash_page_title,
    splash_page_subtitle: agentData.splash_page_subtitle || defaults.splash_page_subtitle,
    start_button_text: agentData.start_button_text || defaults.start_button_text,
    
    avatar_url: agentData.avatar_url || defaults.avatar_url,
    
    ai_temperature: agentData.ai_temperature ?? defaults.ai_temperature,
    
    allow_file_search: agentData.allow_file_search || defaults.allow_file_search,
    allow_web_search: agentData.allow_web_search || defaults.allow_web_search,
    
    is_active: agentData.is_active ?? defaults.is_active,
    is_template: agentData.is_template ?? defaults.is_template,
    is_public: agentData.is_public ?? defaults.is_public,
    goal: agentData.goal || defaults.goal,
    category: agentData.category || defaults.category,
    
    notification_email: agentData.notification_email || defaults.notification_email,
    
    // Industry ID from organization if not set
    industry_id: agentData.industry_id || organizationIndustryId,
    
    // Contact Info Settings
    contact_info: {
      requireName: agentData.contact_info?.requireName ?? defaults.contact_info.requireName,
      requireEmail: agentData.contact_info?.requireEmail ?? defaults.contact_info.requireEmail,
      requirePhoneNumber: agentData.contact_info?.requirePhoneNumber ?? defaults.contact_info.requirePhoneNumber
    },
    
    // Keep all other existing properties
    ...agentData,
  };
};

/**
 * Default form fields that should be created for every new agent
 * 
 * Note: Contact info (name, email, phone) is handled by built-in agent settings
 * in the contact_info field, not as custom form fields. This function should
 * only return additional custom fields that users might want by default.
 */
export const getDefaultFormFields = () => [
  // Return empty array - contact info is handled by built-in agent settings
  // Custom form fields should be added manually by users through the form fields tab
]; 